import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;

const AccountDeletionConfirmation: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired'>('loading');
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid account deletion link. No token provided.');
      return;
    }

    // Add a small delay to ensure proper loading
    const timer = setTimeout(() => {
      // Confirm account deletion
      const confirmAccountDeletion = async () => {
        if (isProcessing) return; // Prevent multiple calls
        
        setIsProcessing(true);
        try {
        const response = await axios.post(`${API_BASE_URL}/email/confirm-account-deletion`, {
          token
        });

        if (response.data.success) {
          setStatus('success');
          setMessage('Your account has been successfully deleted. All your data has been permanently removed.');
          
          // Clear any stored user data
          localStorage.removeItem('fragrance-organizer-user');
          localStorage.removeItem('fragrance-organizer-token');
          
          // Redirect to home after 3 seconds
          setTimeout(() => {
            navigate('/');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Account deletion failed');
        }
      } catch (error: any) {
        console.error('Account deletion confirmation error:', error);
        
        if (error.response?.status === 400) {
          if (error.response.data.message.includes('expired')) {
            setStatus('expired');
            setMessage('This account deletion link has expired. Please request a new one.');
          } else {
            setStatus('error');
            setMessage(error.response.data.message || 'Invalid account deletion link');
          }
        } else {
          setStatus('error');
          setMessage('Network error. Please try again later.');
        }
        } finally {
          setIsProcessing(false);
        }
      };

      confirmAccountDeletion();
    }, 100); // 100ms delay to ensure proper initialization

    return () => clearTimeout(timer);
  }, [location.search, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <LoadingSpinner size="lg" className="mx-auto" />;
      case 'success':
        return <div className="text-6xl mb-4">✅</div>;
      case 'error':
        return <div className="text-6xl mb-4">❌</div>;
      case 'expired':
        return <div className="text-6xl mb-4">⏰</div>;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600 dark:text-green-400';
      case 'error':
      case 'expired':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
      >
        {getStatusIcon()}
        
        <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
          {status === 'loading' && 'Deleting Account...'}
          {status === 'success' && 'Account Deleted'}
          {status === 'error' && 'Deletion Failed'}
          {status === 'expired' && 'Link Expired'}
        </h1>
        
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          {message}
        </p>
        
        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">
              You will be redirected to the home page in a few seconds.
            </p>
          </motion.div>
        )}
        
        {(status === 'error' || status === 'expired') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              onClick={() => navigate('/')}
              className="w-full"
            >
              Return to Home
            </Button>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default AccountDeletionConfirmation;
